/**
 * MarketingHero — canonical hero band for marketing-website pages.
 *
 * 5 variants:
 *   - `simple`    : neutral background, centered eyebrow + title + subtitle + actions (default)
 *   - `split`     : 2-column grid; visual on one side, content on the other
 *   - `centered`  : full-width brand-gradient with inverse text (`.hero-primary`)
 *   - `bleed`     : full-bleed background image/color, no edge padding
 *   - `videoBg`   : looping background video with overlay
 *
 * Consumes the canonical `.hero` family from @puffin-core/shared-styles
 * (navigation.css). Variant is a closed enum; PropTypes enforces it.
 *
 * Actions render through `<SmartLink>` (LIFE-SHORTLISTED-001 F-P11-2): a route stays a router link, an
 * anchor a plain link, and an address on another site a real link with a safe `rel` — a router
 * `<Link to>` had made an off-site button an in-app path that never loaded. `onOutboundClick` reports
 * a click that leaves the site, for the page's own analytics.
 *
 * H1: this component owns the page H1. Pages that mount <MarketingHero>
 * should NOT render another <h1>; the marketing-website's heading hierarchy
 * test asserts exactly one h1 per page.
 *
 * @module @puffin-core/marketing-shell/MarketingHero
 */

import PropTypes from 'prop-types';

import { SmartLink } from '../links/SmartLink.jsx';

const VARIANTS = Object.freeze(['simple', 'split', 'centered', 'bleed', 'videoBg']);

function variantClassName(variant) {
  switch (variant) {
    case 'centered':
      return 'hero hero-primary';
    case 'split':
      return 'hero';
    case 'bleed':
      return 'hero hero-bleed';
    case 'videoBg':
      return 'hero hero-video-bg';
    case 'simple':
    default:
      return 'hero';
  }
}

function HeroActions({ primary, secondary, onOutboundClick }) {
  if (!primary && !secondary) return null;
  return (
    <div className="hero-actions">
      {primary ? (
        <SmartLink
          href={primary.to}
          onClick={primary.onClick}
          onOutboundClick={onOutboundClick}
          className="btn btn-marketing-lg btn-marketing-primary"
        >
          {primary.label}
        </SmartLink>
      ) : null}
      {secondary ? (
        <SmartLink
          href={secondary.to}
          onClick={secondary.onClick}
          onOutboundClick={onOutboundClick}
          className="btn btn-marketing-lg btn-marketing-secondary"
        >
          {secondary.label}
        </SmartLink>
      ) : null}
    </div>
  );
}

HeroActions.propTypes = {
  // PROTECTOR-MKT-002 (2026-05-24) added optional `onClick` to support
  // CTA-click analytics on shared hero buttons. Existing consumers that
  // don't pass onClick are unaffected (undefined → no-op).
  primary: PropTypes.shape({
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }),
  secondary: PropTypes.shape({
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }),
  onOutboundClick: PropTypes.func,
};

/**
 * @param {Object} props
 * @param {'simple'|'split'|'centered'|'bleed'|'videoBg'} [props.variant='simple']
 * @param {string} [props.eyebrow]    Small uppercase kicker above the title.
 * @param {string} props.title        H1 of the page.
 * @param {string} [props.subtitle]
 * @param {string[]} [props.highlights] Short trust-signal claims rendered as a
 *   scannable chip row (.hero-highlights) between the subtitle and the actions.
 * @param {{label, to}} [props.primaryAction]
 * @param {{label, to}} [props.secondaryAction]
 * @param {React.ReactNode} [props.visual] Right-side image / illustration; only honored when variant === 'split'.
 * @param {string} [props.videoSrc]   URL of the looping background video; required when variant === 'videoBg'.
 * @param {string} [props.videoPoster] Poster image for the video element.
 * @param {string} [props.ariaLabel]  Overrides the section's aria-label (defaults to the title).
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick] A click on an
 *   action that leaves the site (`<SmartLink onOutboundClick>`).
 */
export default function MarketingHero({
  variant = 'simple',
  eyebrow,
  title,
  subtitle,
  highlights,
  primaryAction,
  secondaryAction,
  visual,
  videoSrc,
  videoPoster,
  ariaLabel,
  id,
  onOutboundClick,
}) {
  const containerClass = variantClassName(variant);
  const labelledAs = ariaLabel || title;
  const sectionExtras = id ? { id } : {};
  const hasHighlights = Array.isArray(highlights) && highlights.length > 0;

  const content = (
    <div className="hero-content">
      {eyebrow ? <span className="hero-eyebrow">{eyebrow}</span> : null}
      <h1 className="hero-title">{title}</h1>
      {subtitle ? <p className="hero-subtitle">{subtitle}</p> : null}
      {hasHighlights ? (
        <ul className="hero-highlights">
          {highlights.map((highlight) => (
            <li key={highlight} className="hero-highlight">
              {highlight}
            </li>
          ))}
        </ul>
      ) : null}
      <HeroActions
        primary={primaryAction}
        secondary={secondaryAction}
        onOutboundClick={onOutboundClick}
      />
    </div>
  );

  if (variant === 'split') {
    return (
      <section className={containerClass} aria-label={labelledAs} {...sectionExtras}>
        <div className="hero-grid">
          {content}
          {visual ? <div className="hero-visual">{visual}</div> : null}
        </div>
      </section>
    );
  }

  if (variant === 'videoBg') {
    return (
      <section className={containerClass} aria-label={labelledAs} {...sectionExtras}>
        {videoSrc ? (
          <video
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={videoPoster}
            aria-hidden="true"
          >
            <source src={videoSrc} />
          </video>
        ) : null}
        {content}
      </section>
    );
  }

  return (
    <section className={containerClass} aria-label={labelledAs} {...sectionExtras}>
      {content}
    </section>
  );
}

MarketingHero.propTypes = {
  variant: PropTypes.oneOf(VARIANTS),
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  highlights: PropTypes.arrayOf(PropTypes.string),
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  }),
  visual: PropTypes.node,
  videoSrc: PropTypes.string,
  videoPoster: PropTypes.string,
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
  onOutboundClick: PropTypes.func,
};

MarketingHero.VARIANTS = VARIANTS;
